import { Pipe, PipeTransform } from '@angular/core';

/**
 * Formatting rules for one currency:
 *  - display: what is rendered next to the amount (an icon like "$"/"€" or a
 *    textual code like "Rs.").
 *  - icon: icons are glued to the amount ("$1,234.56"); textual codes get a
 *    non-breaking space between code and amount ("Rs. 1,234.56").
 *  - decimals / thousands / decimal: number style per the currency's standard.
 *  - hideZeroDecimals: drop the decimal part entirely when it is zero
 *    (e.g. "XPF 1 234" but "XPF 1 234,50").
 */
interface CurrencyFormat {
    display: string;
    icon: boolean;
    decimals: number;
    thousands: string;
    decimal: string;
    hideZeroDecimals?: boolean;
}

const NBSP = ' ';

// English convention: 1,234.56 — French convention: 1 234,56
const CURRENCY_FORMATS: { [code: string]: CurrencyFormat } = {
    // US Dollar: $1,234.56
    'USD': { display: '$', icon: true, decimals: 2, thousands: ',', decimal: '.' },
    // Euro (French territories): €1 234,56
    'EUR': { display: '€', icon: true, decimals: 2, thousands: NBSP, decimal: ',' },
    // CFP Franc: French convention, decimals only when non-zero — XPF 1 234 / XPF 1 234,50
    'XPF': { display: 'XPF', icon: false, decimals: 2, thousands: NBSP, decimal: ',', hideZeroDecimals: true },
    'CFP': { display: 'CFP', icon: false, decimals: 2, thousands: NBSP, decimal: ',', hideZeroDecimals: true },
    // Mauritian Rupee: English convention — Rs. 1,234.56
    'RS.': { display: 'Rs.', icon: false, decimals: 2, thousands: ',', decimal: '.' },
    'RS': { display: 'Rs.', icon: false, decimals: 2, thousands: ',', decimal: '.' },
    'MUR': { display: 'Rs.', icon: false, decimals: 2, thousands: ',', decimal: '.' },
    // Malagasy Ariary: no practical decimal subunit, French convention — Ar. 1 234
    'AR.': { display: 'Ar.', icon: false, decimals: 0, thousands: NBSP, decimal: ',' },
    'AR': { display: 'Ar.', icon: false, decimals: 0, thousands: NBSP, decimal: ',' },
    'MGA': { display: 'Ar.', icon: false, decimals: 0, thousands: NBSP, decimal: ',' },
};

/**
 * Formats an amount with its currency according to that currency's standard.
 * Currencies not in the list fall back to the English convention with the
 * given code rendered as text before the amount (e.g. "Y 9.80"); when no code
 * is provided at all, USD is assumed (matching Angular's currency pipe
 * default, which this pipe previously wrapped).
 *
 * Usage mirrors the built-in pipe: {{ value | appCurrency:currencyCode }}
 */
@Pipe({ name: 'appCurrency', standalone: false })
export class AppCurrencyPipe implements PipeTransform {

    transform(value: number | string | null | undefined, currencyCode?: string): string | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        const amount = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(amount)) {
            return null;
        }

        const code = (currencyCode || '').toString().trim();
        const format = CURRENCY_FORMATS[code.toUpperCase()]
            || (code
                ? { display: code, icon: false, decimals: 2, thousands: ',', decimal: '.' }
                : CURRENCY_FORMATS['USD']);

        const sign = amount < 0 ? '-' : '';
        const number = this.formatNumber(Math.abs(amount), format);
        return format.icon
            ? sign + format.display + number
            : sign + format.display + NBSP + number;
    }

    private formatNumber(value: number, format: CurrencyFormat): string {
        const [integer, fraction] = value.toFixed(format.decimals).split('.');
        const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, format.thousands);
        if (!fraction || (format.hideZeroDecimals && /^0+$/.test(fraction))) {
            return grouped;
        }
        return grouped + format.decimal + fraction;
    }
}
